import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Bell, Megaphone, PaperPlaneTilt, Info, CheckCircle, Warning, X as XIcon,
  Users as UsersIcon, MagnifyingGlass, ListChecks,
} from "@phosphor-icons/react";

const TYPES = [
  { k: "info", label: "Info", color: "#007AFF", icon: Info },
  { k: "success", label: "Éxito", color: "#32D74B", icon: CheckCircle },
  { k: "warning", label: "Aviso", color: "#FFD60A", icon: Warning },
  { k: "critical", label: "Crítico", color: "#FF3B30", icon: Warning },
];

const TEMPLATES = [
  { name: "Recordatorio wellness", type: "info", title: "¿Cómo te sentís esta semana?", body: "Completá tu cuestionario wellness de 30 segundos.", link: "/profile/{user_id}?tab=wellness" },
  { name: "Rutina actualizada", type: "success", title: "Rutina actualizada", body: "El entrenador cargó una nueva rutina para vos. Revisala en tu perfil.", link: "/profile/{user_id}?tab=routines" },
  { name: "Plan nutricional nuevo", type: "success", title: "Nuevo plan nutricional", body: "Se actualizó tu plan nutricional. Chequeá los objetivos calóricos.", link: "/profile/{user_id}?tab=nutrition" },
  { name: "Testeo programado", type: "info", title: "Testeo físico esta semana", body: "Tenés un testeo programado. Descansá bien e hidratate el día previo.", link: "/profile/{user_id}?tab=tests" },
  { name: "Felicitaciones PR", type: "success", title: "Nuevo récord personal", body: "Superaste tu mejor marca. ¡Gran trabajo!", link: "/profile/{user_id}?tab=tests" },
  { name: "Recordatorio de pago", type: "warning", title: "Recordatorio de pago", body: "Es momento de abonar tu mensualidad. Abrí la solapa Pagos.", link: "/payments" },
];

const LINK_OPTIONS = [
  { value: "", label: "Sin link (solo notificación)" },
  { value: "/profile/{user_id}?tab=profile", label: "Perfil del atleta" },
  { value: "/profile/{user_id}?tab=tests", label: "Testeos físicos" },
  { value: "/profile/{user_id}?tab=routines", label: "Rutinas" },
  { value: "/profile/{user_id}?tab=nutrition", label: "Nutrición" },
  { value: "/profile/{user_id}?tab=wellness", label: "Wellness" },
  { value: "/payments", label: "Pagos" },
  { value: "/dashboard", label: "Dashboard" },
];

export default function AdminNotificationsTab() {
  const [mode, setMode] = useState("broadcast"); // "broadcast" | "individual"
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]); // for individual multi-select
  const [form, setForm] = useState({ title: "", body: "", type: "info", link: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/users")
      .then((r) => setUsers(r.data.filter((u) => u.role !== "admin")))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const filteredUsers = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s),
    );
  }, [users, search]);

  const toggleUser = (uid) => {
    setSelectedIds((prev) => prev.includes(uid)
      ? prev.filter((x) => x !== uid)
      : [...prev, uid]);
  };
  const selectAllVisible = () => setSelectedIds(filteredUsers.map((u) => u.user_id));
  const clearSelection = () => setSelectedIds([]);

  const applyTemplate = (tpl) => setForm({ title: tpl.title, body: tpl.body, type: tpl.type, link: tpl.link || "" });

  const send = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Ingresá un título");
      return;
    }
    if (mode === "individual" && selectedIds.length === 0) {
      toast.error("Seleccioná al menos un atleta");
      return;
    }
    setSending(true);
    try {
      if (mode === "broadcast") {
        const { data } = await api.post("/admin/notifications/broadcast", form);
        toast.success(`Notificación enviada a ${data.sent} atletas`);
      } else {
        let ok = 0;
        for (const uid of selectedIds) {
          try {
            await api.post(`/users/${uid}/notifications`, form);
            ok++;
          } catch (err) {
            console.warn("Falló envío a", uid, err);
          }
        }
        toast.success(`Notificación enviada a ${ok} de ${selectedIds.length} atletas`);
      }
      setForm({ title: "", body: "", type: "info", link: "" });
      setSelectedIds([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-tab">
      <div className="card-base p-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={22} weight="duotone" className="text-primary-app" />
          <h2 className="font-display font-black uppercase text-2xl tracking-tighter">Enviar notificación</h2>
        </div>

        {/* Mode toggle */}
        <div className="inline-flex border border-app mb-5" data-testid="notif-mode">
          <button
            type="button"
            onClick={() => setMode("broadcast")}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 transition-colors ${
              mode === "broadcast" ? "bg-[#FF3B30] text-white" : "bg-transparent text-muted hover:text-white"
            }`}
            data-testid="notif-mode-broadcast"
          >
            <Megaphone size={14} weight="bold" /> A todos
          </button>
          <button
            type="button"
            onClick={() => setMode("individual")}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 transition-colors ${
              mode === "individual" ? "bg-[#FF3B30] text-white" : "bg-transparent text-muted hover:text-white"
            }`}
            data-testid="notif-mode-individual"
          >
            <UsersIcon size={14} weight="bold" /> Atletas específicos
          </button>
        </div>

        {/* Templates */}
        <div className="mb-5" data-testid="notif-templates">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-2 flex items-center gap-1">
            <ListChecks size={11} weight="bold" /> Plantillas rápidas
          </div>
          <div className="flex gap-2 flex-wrap">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.name}
                type="button"
                onClick={() => applyTemplate(tpl)}
                className="text-[11px] uppercase tracking-widest font-bold border border-app px-3 py-1.5 hover:!border-white text-muted hover:text-white"
                data-testid={`tpl-${tpl.name}`}
              >
                {tpl.name}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={send} className="space-y-4" data-testid="notif-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Título *</label>
            <input
              className="input-base"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej: Cambio en el horario de mañana"
              required
              data-testid="notif-title"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Mensaje</label>
            <textarea
              className="input-base"
              rows={3}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Detalles del mensaje (opcional)"
              data-testid="notif-body"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Tipo</label>
              <div className="flex gap-2 flex-wrap">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.type === t.k;
                  return (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setForm({ ...form, type: t.k })}
                      className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold border px-3 py-2 transition-colors"
                      style={active ? { background: t.color, borderColor: t.color, color: t.k === "warning" ? "#000" : "#FFF" } : { borderColor: "#262626", color: "#A3A3A3" }}
                      data-testid={`notif-type-${t.k}`}
                    >
                      <Icon size={12} weight="fill" /> {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Link de destino (opcional)</label>
              <select
                className="input-base"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                data-testid="notif-link"
              >
                {LINK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="text-[10px] text-muted mt-1">
                {form.link.includes("{user_id}") && "Se reemplaza automáticamente por el id del atleta destinatario."}
              </div>
            </div>
          </div>

          {/* Selector de atletas (solo modo individual) */}
          {mode === "individual" && (
            <div data-testid="notif-athlete-picker">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <label className="text-xs uppercase tracking-widest text-muted">
                  Atletas seleccionados · {selectedIds.length}
                </label>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllVisible} className="text-[10px] uppercase tracking-widest text-muted hover:text-white">
                    Seleccionar visibles
                  </button>
                  {selectedIds.length > 0 && (
                    <button type="button" onClick={clearSelection} className="text-[10px] uppercase tracking-widest text-muted hover:text-[#FF3B30]">
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
              <div className="relative mb-2">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  className="input-base pl-9"
                  placeholder="Buscar atleta..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="notif-athlete-search"
                />
              </div>
              <div className="border border-app max-h-64 overflow-y-auto" data-testid="notif-athlete-list">
                {loading ? (
                  <div className="text-xs text-muted py-4 text-center">Cargando...</div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-xs text-muted py-6 text-center">Sin atletas {search ? "que coincidan" : ""}.</div>
                ) : filteredUsers.map((u) => {
                  const active = selectedIds.includes(u.user_id);
                  return (
                    <label
                      key={u.user_id}
                      className={`flex items-center gap-3 px-3 py-2 border-b border-app cursor-pointer transition-colors ${active ? "bg-[#FF3B30]/10" : "hover:bg-[#0A0A0A]"}`}
                      data-testid={`notif-pick-${u.user_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleUser(u.user_id)}
                        className="accent-[#FF3B30]"
                      />
                      <div className="w-8 h-8 bg-[#262626] flex items-center justify-center text-xs font-bold text-white overflow-hidden flex-shrink-0">
                        {u.profile?.avatar_url ? <img src={u.profile.avatar_url} alt="" className="w-full h-full object-cover" /> : (u.name?.[0] || "?")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold truncate">{u.name}</div>
                        <div className="text-[11px] text-muted truncate">{u.email}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={sending}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
            data-testid="notif-send"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            {sending ? "Enviando..." : mode === "broadcast" ? "Enviar a todos los atletas" : `Enviar a ${selectedIds.length || ""} atleta${selectedIds.length === 1 ? "" : "s"}`}
          </button>
        </form>
      </div>
    </div>
  );
}
